import React, { useState, useEffect, useLayoutEffect, useRef, lazy, Suspense } from 'react';
import './App.css';
// Eager: entry-page (Landing) + globaalne chrome, mis renderdub igal lehel
// (LanguageSelector, Logo, FAQ, Contact, CookieConsent). Need peavad kohe olemas olema.
import Landing from './components/Landing';
import LanguageSelector from './components/LanguageSelector';
import Logo from './components/Logo';
import FAQ from './components/FAQ';
import Contact from './components/Contact';
import CookieConsent from './components/CookieConsent';
import { LanguageProvider, useTranslation } from './LanguageContext';
import { trackEvent } from './stats';
// Lazy: page-komponendid, mida cold landing-külastaja kohe ei vaja. Korraga renderdub
// ainult üks page (page === 'X'), seega React.lazy + <Suspense> on madala riskiga.
// Boonus: react-markdown (ainult Demo.js-is) liigub Demo chunki, kaob landing-laadingust.
const Register = lazy(() => import('./components/Register'));
const Login = lazy(() => import('./components/Login'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const Intro = lazy(() => import('./components/Intro'));
const Test = lazy(() => import('./components/Test'));
const PairCode = lazy(() => import('./components/PairCode'));
const EmailKinnitatud = lazy(() => import('./components/EmailKinnitatud'));
const ParoolLahtesta = lazy(() => import('./components/ParoolLahtesta'));
const MakseEdukas = lazy(() => import('./components/MakseEdukas'));
const MakseKatkestatud = lazy(() => import('./components/MakseKatkestatud'));
const DeleteAccount = lazy(() => import('./components/DeleteAccount'));
const Demo = lazy(() => import('./components/Demo'));
const References = lazy(() => import('./components/References'));
const SoloDashboard = lazy(() => import('./components/SoloDashboard'));
const Terms = lazy(() => import('./components/Terms'));

// ARENDUSREŽIIM
const DEV_START_PAGE = 'landing';
const DEV_MODE = false;

const SEO_META = {
  et: {
    title: 'Inoroff – kus paarid avastavad oma tõe',
    description: 'Inoroff on paaride ühilduvuse analüüs. Mõlemad partnerid vastavad küsimustele iseseisvalt ja saavad personaalse AI analüüsi oma suhte kohta.',
  },
  en: {
    title: 'Inoroff – Where Couples Discover Their Truth',
    description: 'Inoroff is a deep compatibility and desires analysis for couples. Both partners answer questions independently and receive a personalized AI analysis of their relationship.',
  },
  ru: {
    title: 'Inoroff – где пары открывают свою правду',
    description: 'Inoroff — это анализ совместимости для пар. Оба партнёра отвечают на вопросы независимо и получают персональный AI-анализ своих отношений.',
  },
  pl: {
    title: 'Inoroff – Gdzie Pary Odkrywają Swoją Prawdę',
    description: 'Inoroff to analiza kompatybilności dla par. Oboje partnerzy odpowiadają na pytania niezależnie i otrzymują spersonalizowaną analizę AI swojego związku.',
  },
  de: {
    title: 'Inoroff – Wo Paare Ihre Wahrheit Entdecken',
    description: 'Inoroff ist eine Kompatibilitätsanalyse für Paare. Beide Partner beantworten Fragen unabhängig und erhalten eine personalisierte KI-Analyse ihrer Beziehung.',
  },
  fr: {
    title: 'Inoroff – Où les Couples Découvrent Leur Vérité',
    description: 'Inoroff est une analyse de compatibilité pour les couples. Les deux partenaires répondent à des questions indépendamment et reçoivent une analyse IA personnalisée de leur relation.',
  },
  es: {
    title: 'Inoroff – Donde las Parejas Descubren Su Verdad',
    description: 'Inoroff es un análisis de compatibilidad para parejas. Ambos responden preguntas de forma independiente y reciben un análisis personalizado con IA de su relación.',
  },
  it: {
    title: 'Inoroff – Dove le Coppie Scoprono la Loro Verità',
    description: 'Inoroff è un\'analisi di compatibilità per coppie. Entrambi i partner rispondono a domande in modo indipendente e ricevono un\'analisi AI personalizzata della loro relazione.',
  },
  pt: {
    title: 'Inoroff – Onde os Casais Descobrem a Sua Verdade',
    description: 'Inoroff é uma análise de compatibilidade para casais. Ambos os parceiros respondem a perguntas de forma independente e recebem uma análise personalizada com IA da sua relação.',
  },
  nl: {
    title: 'Inoroff – Waar Koppels Hun Waarheid Ontdekken',
    description: 'Inoroff is een compatibiliteitsanalyse voor koppels. Beide partners beantwoorden vragen onafhankelijk en ontvangen een gepersonaliseerde AI-analyse van hun relatie.',
  },
  sv: {
    title: 'Inoroff – Där Par Upptäcker Sin Sanning',
    description: 'Inoroff är en kompatibilitetsanalys för par. Båda partnerna svarar på frågor oberoende och får en personlig AI-analys av sin relation.',
  },
  no: {
    title: 'Inoroff – Der Par Oppdager Sin Sannhet',
    description: 'Inoroff er en kompatibilitetsanalyse for par. Begge partnerne svarer på spørsmål uavhengig og mottar en personlig AI-analyse av forholdet sitt.',
  },
  da: {
    title: 'Inoroff – Hvor Par Opdager Deres Sandhed',
    description: 'Inoroff er en kompatibilitetsanalyse for par. Begge partnere besvarer spørgsmål uafhængigt og modtager en personlig AI-analyse af deres forhold.',
  },
  fi: {
    title: 'Inoroff – Missä Parit Löytävät Totuutensa',
    description: 'Inoroff on yhteensopivuusanalyysi pareille. Molemmat kumppanit vastaavat kysymyksiin itsenäisesti ja saavat henkilökohtaisen tekoälyanalyysin suhteestaan.',
  },
  cs: {
    title: 'Inoroff – Kde Páry Objevují Svou Pravdu',
    description: 'Inoroff je analýza kompatibility pro páry. Oba partneři odpovídají na otázky nezávisle a obdrží personalizovanou AI analýzu svého vztahu.',
  },
  sk: {
    title: 'Inoroff – Kde Páry Objavujú Svoju Pravdu',
    description: 'Inoroff je analýza kompatibility pre páry. Obaja partneri odpovedajú na otázky nezávisle a dostanú personalizovanú AI analýzu svojho vzťahu.',
  },
  hu: {
    title: 'Inoroff – Ahol a Párok Felfedezik Igazságukat',
    description: 'Az Inoroff egy kompatibilitási elemzés párok számára. Mindkét partner kérdésekre válaszol függetlenül, és személyre szabott AI-elemzést kap a kapcsolatukról.',
  },
  ro: {
    title: 'Inoroff – Unde Cuplurile Își Descoperă Adevărul',
    description: 'Inoroff este o analiză de compatibilitate pentru cupluri. Ambii parteneri răspund la întrebări independent și primesc o analiză AI personalizată a relației lor.',
  },
  lv: {
    title: 'Inoroff – Kur Pāri Atklāj Savu Patiesību',
    description: 'Inoroff ir saderības analīze pāriem. Abi partneri neatkarīgi atbild uz jautājumiem un saņem personalizētu AI analīzi par savām attiecībām.',
  },
  lt: {
    title: 'Inoroff – Kur Poros Atranda Savo Tiesą',
    description: 'Inoroff yra suderinamumo analizė poroms. Abu partneriai nepriklausomai atsako į klausimus ir gauna personalizuotą AI analizę apie savo santykius.',
  },
  el: {
    title: 'Inoroff – Όπου τα ζευγάρια ανακαλύπτουν την αλήθεια τους',
    description: 'Το Inoroff είναι μια ανάλυση συμβατότητας για ζευγάρια. Και οι δύο σύντροφοι απαντούν ανεξάρτητα σε ερωτήσεις και λαμβάνουν μια εξατομικευμένη ανάλυση AI της σχέσης τους.',
  },
  tl: {
    title: 'Inoroff – Dito Nalaman ng mga Mag-asawa ang Kanilang Katotohanan',
    description: 'Ang Inoroff ay isang compatibility analysis para sa mga mag-asawa at couples. Parehong sumasagot ng mga tanong nang hiwalay at tumatanggap ng personalized na AI analysis ng kanilang relasyon.',
  },
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function AppInner() {
  const { t, lang } = useTranslation();
  const [contactOpen, setContactOpen] = useState(false);
  // "Mis edasi?" nupud: PairCode saadab kasutaja solo dashboardile koos sooviga
  // avada kohe pair/friend ühendamise modaal ('pair' | 'friend' | null)
  const [soloIntent, setSoloIntent] = useState(null);
  const [termsOpen, setTermsOpen] = useState(false);
  const [maintenance, setMaintenance] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(true);
  // Token aegus (nt 7-päevane JWT) → näita login-lehel sõbralikku "sessioon aegus" teadet
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    fetch(API_URL + '/api/settings')
      .then(r => r.json())
      .then(s => {
        setMaintenance(s.maintenance_mode === 'true');
        setRegistrationOpen(s.registration_open !== 'false');
      })
      .catch(() => {});
  }, []);

  // Uuenda SEO meta tagid keele järgi
  useEffect(() => {
    const meta = SEO_META[lang] || SEO_META.en;
    document.title = meta.title;
    document.documentElement.lang = lang;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', meta.description);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', meta.description);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', meta.title);
    const twTitle = document.querySelector('meta[name="twitter:title"]');
    if (twTitle) twTitle.setAttribute('content', meta.title);
    const twDesc = document.querySelector('meta[name="twitter:description"]');
    if (twDesc) twDesc.setAttribute('content', meta.description);
  }, [lang]);

  const [page, setPage] = useState(() => {
    const path = window.location.pathname.replace(/^\//, '') || 'landing';
    const specialPaths = ['email-kinnitatud', 'parool-lahtesta', 'makse-edukas', 'makse-katkestatud'];
    if (specialPaths.includes(path)) return path;
    const authPages = ['dashboard', 'solo', 'intro', 'test', 'paircode', 'delete-account'];
    const publicPages = ['landing', 'register', 'login', 'forgot', 'demo', 'references'];
    const hasToken = !!localStorage.getItem('token');
    if (authPages.includes(path) && hasToken) return path;
    if (publicPages.includes(path)) return path;
    return DEV_MODE ? DEV_START_PAGE : (hasToken ? 'solo' : 'landing');
  });

  // BUG-FIX: keri alati lehe ülaossa PÄRAST uue lehe renderdust (mitte enne, nagu
  // varem navigate()-s — siis jäi scroll eelmise lehe asukohta, nt Solo nupp landingu
  // keskel → register-leht avanes footeriga, mitte vormiga).
  useLayoutEffect(() => { window.scrollTo(0, 0); }, [page]);

  const navigate = (newPage) => {
    // Saada eelmise lehe veedetud aeg
    const startTime = window.__pageStartTime;
    const prevPage = window.__currentPage;
    if (startTime && prevPage) {
      const duration = Date.now() - startTime;
      if (duration > 500 && duration < 3600000) { // 0.5s - 1h, ignoreeri liiga lühikesi
        trackEvent('page_time', { value: prevPage, duration_ms: duration });
      }
    }
    // Lahkudes login-lehelt peida "sessioon aegus" teade
    if (newPage !== 'login') setSessionExpired(false);
    setPage(newPage);
    window.history.pushState({ page: newPage }, '', '/' + newPage);
    trackEvent('page_view', { value: newPage });
    window.__pageStartTime = Date.now();
    window.__currentPage = newPage;
  };

  // Esimese lehe laadimisel saada page_view (navigate() ei käivitu esimesel korral)
  useEffect(() => {
    trackEvent('page_view', { value: page });
    // eslint-disable-next-line
  }, []);

  // Saada page_time ka kui kasutaja sulgeb akna
  useEffect(() => {
    window.__pageStartTime = Date.now();
    window.__currentPage = page;
    const handleUnload = () => {
      const startTime = window.__pageStartTime;
      const currentPage = window.__currentPage;
      if (startTime && currentPage) {
        const duration = Date.now() - startTime;
        if (duration > 500 && duration < 3600000) {
          // Kasuta sendBeacon et kindlasti jõuaks
          const payload = JSON.stringify({
            session_id: localStorage.getItem('sid'),
            event: 'page_time',
            value: currentPage,
            duration_ms: duration,
            language: localStorage.getItem('language') || 'en',
          });
          navigator.sendBeacon((process.env.REACT_APP_API_URL || '') + '/api/stats/event', new Blob([payload], { type: 'application/json' }));
        }
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const specialPaths = {
      '/email-kinnitatud': 'email-kinnitatud',
      '/parool-lahtesta': 'parool-lahtesta',
      '/makse-edukas': 'makse-edukas',
      '/makse-katkestatud': 'makse-katkestatud',
    };
    const special = specialPaths[window.location.pathname];
    if (special) {
      setPage(special);
    } else {
      window.history.replaceState({ page }, '', '/' + page);
    }

    const handlePop = (e) => {
      if (e.state?.page) setPage(e.state.page);
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    trackEvent('account_logout');
    localStorage.removeItem('token');
    // Lähtesta ka session-spetsiifilised flag'id (test_first_answer, test_completed)
    localStorage.removeItem('test_first_answer_fired');
    localStorage.removeItem('test_completed_fired');
    navigate('login');
  };

  // ── Inaktiivsuse automaatne väljalogimine ──
  // 2h ilma tegevuseta (klõps/kerimine/klaviatuur) → 60 sek hoiatus → logout.
  const INACTIVITY_LIMIT_MS = 2 * 60 * 60 * 1000;
  const LOGOUT_WARNING_S = 60;
  const [inactivityWarning, setInactivityWarning] = useState(false);
  const [logoutCountdown, setLogoutCountdown] = useState(LOGOUT_WARNING_S);
  const inactivityTimer = useRef(null);
  const warningShownRef = useRef(false);

  const armInactivityTimer = () => {
    clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      // Hoiatus ainult sisselogitud kasutajale; muidu hoia taimer käimas
      if (!localStorage.getItem('token')) { armInactivityTimer(); return; }
      warningShownRef.current = true;
      setLogoutCountdown(LOGOUT_WARNING_S);
      setInactivityWarning(true);
    }, INACTIVITY_LIMIT_MS - LOGOUT_WARNING_S * 1000);
  };

  useEffect(() => {
    const onActivity = () => {
      // Hoiatuse ajal ei loe taustategevus — ainult "Jään sisse" nupp
      if (warningShownRef.current) return;
      armInactivityTimer();
    };
    const events = ['click', 'scroll', 'keydown', 'touchstart'];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    armInactivityTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, onActivity));
      clearTimeout(inactivityTimer.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Hoiatuse countdown: 60 → 0 → automaatne logout
  useEffect(() => {
    if (!inactivityWarning) return undefined;
    const iv = setInterval(() => {
      setLogoutCountdown(c => {
        if (c <= 1) {
          clearInterval(iv);
          warningShownRef.current = false;
          setInactivityWarning(false);
          handleLogout();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [inactivityWarning]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStayLoggedIn = () => {
    warningShownRef.current = false;
    setInactivityWarning(false);
    armInactivityTimer();
  };

  // ── Token aegumise automaatne käsitlus ──
  // Kui mõni API-päring tagastab 401 (nt server-side JWT on aegunud või muutus
  // kehtetuks), logime kasutaja automaatselt välja ja suuname login-lehele sõbraliku
  // teatega. Varem jäi UI "sisselogituks" ja päringud ebaõnnestusid vaikselt.
  useEffect(() => {
    const origFetch = window.fetch;
    window.fetch = async (...args) => {
      const res = await origFetch(...args);
      try {
        const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
        // Reageeri ainult oma API 401-le ja ainult kui kasutajal ON token (st oli sisse
        // logitud) — nii ei sega see login/register päringuid, kus tokenit veel pole.
        if (res.status === 401 && url.includes('/api/') && localStorage.getItem('token')) {
          window.dispatchEvent(new Event('inoroff:unauthorized'));
        }
      } catch (e) { /* ignore */ }
      return res;
    };

    const onUnauthorized = () => {
      if (!localStorage.getItem('token')) return;
      localStorage.removeItem('token');
      localStorage.removeItem('test_first_answer_fired');
      localStorage.removeItem('test_completed_fired');
      setSessionExpired(true);
      navigate('login');
    };
    window.addEventListener('inoroff:unauthorized', onUnauthorized);

    return () => {
      window.fetch = origFetch;
      window.removeEventListener('inoroff:unauthorized', onUnauthorized);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Logo klikk: sisselogitud kasutaja → oma dashboard (paariühendusega → pair,
  // muidu solo); väljalogitud → esileht
  const handleLogoClick = () => {
    const tok = localStorage.getItem('token');
    if (!tok) { navigate('landing'); return; }
    fetch(API_URL + '/api/pairs/mycode', { headers: { 'Authorization': `Bearer ${tok}` } })
      .then(res => res.json())
      .then(data => navigate(data.partnerId ? 'dashboard' : 'solo'))
      .catch(() => navigate('solo'));
  };

  if (maintenance) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1a1a2e, #16213e)', color: 'white', textAlign: 'center', padding: '24px' }}>
      <div>
        <p style={{ fontSize: '48px', marginBottom: '16px' }}>🔧</p>
        <h2 style={{ marginBottom: '12px' }}>Hoolduspaus</h2>
        <p style={{ color: 'rgba(255,255,255,0.6)' }}>Inoroff on hetkel hoolduses. Oleme varsti tagasi!</p>
      </div>
    </div>
  );

  return (
    <div className={page === 'landing' || page === 'demo' || page === 'references' ? 'App App--landing' : 'App'}>
      {page !== 'landing' && page !== 'demo' && page !== 'references' && <LanguageSelector />}
      {page !== 'landing' && page !== 'demo' && page !== 'references' && (
        <button
          onClick={handleLogoClick}
          style={{
            position: 'fixed',
            top: '14px',
            left: '14px',
            zIndex: 1000,
            border: 'none',
            background: 'none',
            padding: 0,
            cursor: 'pointer',
            transformOrigin: 'top left',
            transform: 'scale(0.42)',
          }}
        >
          <Logo size={40} showTagline={false} />
        </button>
      )}
      <FAQ />
      <Contact open={contactOpen} onClose={() => setContactOpen(false)} />
      <CookieConsent />
      {termsOpen && <Suspense fallback={null}><Terms onClose={() => setTermsOpen(false)} /></Suspense>}

      {/* Inaktiivsuse hoiatus: 60 sek countdown enne automaatset väljalogimist */}
      {inactivityWarning && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 3000, padding: '16px',
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', padding: '32px',
            maxWidth: '420px', width: '100%', textAlign: 'center',
            boxShadow: '0 4px 30px rgba(0,0,0,0.3)',
          }}>
            <p style={{ fontSize: '36px', marginBottom: '12px' }}>⏳</p>
            <p style={{ color: '#333', fontSize: '15px', lineHeight: '1.6', marginBottom: '24px' }}>
              {t('common.inactivityWarning').replace('{seconds}', logoutCountdown)}
            </p>
            <button
              onClick={handleStayLoggedIn}
              style={{
                width: '100%', background: 'linear-gradient(135deg, #7c3aed, #9333ea)',
                color: 'white', border: 'none', padding: '14px',
                borderRadius: '10px', fontSize: '15px', cursor: 'pointer', fontWeight: '700',
              }}>
              {t('common.stayLoggedIn')}
            </button>
          </div>
        </div>
      )}
      <Suspense fallback={
        <div style={{
          minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'rgba(255,255,255,0.6)', fontSize: '14px',
        }}>
          <span style={{ display: 'inline-block', animation: 'arrowBounce 1.6s ease-in-out infinite' }}>…</span>
        </div>
      }>
        {page === 'demo' && <Demo onRegister={() => navigate('register')} onHome={() => navigate('landing')} />}
        {page === 'references' && <References onHome={() => navigate('landing')} />}
        {page === 'landing' && <Landing onRegister={() => navigate('register')} onLogin={() => navigate('login')} onDemo={() => navigate('demo')} onReferences={() => navigate('references')} />}
        {page === 'register' && <Register onLogin={() => navigate('login')} registrationOpen={registrationOpen} />}
        {page === 'login' && <Login onRegister={() => navigate('register')} onForgot={() => navigate('forgot')} onSuccess={() => navigate('solo')} sessionExpired={sessionExpired} />}
        {page === 'forgot' && <ForgotPassword onBack={() => navigate('login')} />}
        {page === 'dashboard' && <Dashboard onStartTest={() => navigate('intro')} onReviewTest={() => navigate('test')} onLogout={handleLogout} onPairCode={() => navigate('paircode')} onDeleteAccount={() => navigate('delete-account')} onGoSolo={(intent) => { setSoloIntent(intent || null); navigate('solo'); }} />}
        {page === 'solo' && <SoloDashboard onStartTest={() => navigate('intro')} onContinueTest={() => navigate('test')} onPairDashboard={() => navigate('dashboard')} onLogout={handleLogout} onDeleteAccount={() => navigate('delete-account')} initialModal={soloIntent} onConsumeIntent={() => setSoloIntent(null)} />}
        {page === 'intro' && <Intro onStart={() => navigate('test')} onPairCode={() => navigate('paircode')} onLogout={handleLogout} onDeleteAccount={() => navigate('delete-account')} />}
        {page === 'test' && <Test onBack={() => navigate('solo')} onPairCode={() => navigate('paircode')} />}
        {page === 'paircode' && <PairCode onBack={() => navigate('solo')} onDone={() => navigate('dashboard')} onGoSolo={(intent) => { setSoloIntent(intent || null); navigate('solo'); }} />}
        {page === 'delete-account' && <DeleteAccount onBack={() => navigate('solo')} />}
        {page === 'email-kinnitatud' && <EmailKinnitatud />}
        {page === 'parool-lahtesta' && <ParoolLahtesta />}
        {page === 'makse-edukas' && <MakseEdukas onDone={() => navigate('solo')} />}
        {page === 'makse-katkestatud' && <MakseKatkestatud />}
      </Suspense>
      <footer style={{
        textAlign: 'center',
        padding: '24px 0 12px 0',
        fontSize: '12px',
        color: 'rgba(255,255,255,0.5)',
        fontStyle: 'italic'
      }}>
        <p style={{
          maxWidth: '720px', margin: '0 auto 14px', padding: '0 16px',
          fontSize: '11px', color: 'rgba(255,255,255,0.4)', lineHeight: 1.6,
        }}>
          {t('landing.disclaimer')}
        </p>
        Made with <span style={{ color: '#e03' }}>♥</span> by Flirtes OÜ · Reg. 14532344 · Tartu mnt 84a, 10112 Tallinn ·{' '}
        <a href="mailto:info@inoroff.org" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'underline' }}>info@inoroff.org</a>
        {' · '}© 2026 Inoroff ·{' '}
        <button
          onClick={() => setTermsOpen(true)}
          style={{
            background: 'none', border: 'none', padding: 0,
            color: 'rgba(255,255,255,0.5)', textDecoration: 'underline',
            fontSize: '12px', fontStyle: 'italic', cursor: 'pointer',
          }}
        >
          {t('common.terms')}
        </button>
        {' · '}
        <button
          onClick={() => setContactOpen(true)}
          style={{
            background: 'none', border: 'none', padding: 0,
            color: 'rgba(255,255,255,0.5)', textDecoration: 'underline',
            fontSize: '12px', fontStyle: 'italic', cursor: 'pointer',
          }}
        >
          {t('common.contact')}
        </button>
      </footer>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AppInner />
    </LanguageProvider>
  );
}

export default App;